import {
  BUILT_IN_CERTIFICATE_TEMPLATE_ID,
  type CertificateAwardDetails,
} from "./certificateTemplates";
import type { SignatureInput } from "../signatures/signatures";
import type { FamilySearchIndexEntry } from "../../services/api/family";
import type { ParentChild } from "../../types/parentFamily";

/** One use of a published certificate template, e.g. "Maths Olympics – Gold". */
export type Award = Required<CertificateAwardDetails> & {
  awardId: string;
  title: string;
  certificateTemplateId: string;
  /** Short code at the start of each certificate ID, e.g. MATH. */
  idPrefix: string;
};

/** An Award as it's loaded. Ones saved before signatures existed have none. */
export type SavedAward = Omit<Award, "signatures"> & {
  signatures?: SignatureInput[];
};

/** What a Team member fills in for an Award. The server adds the ID. */
export type AwardDetails = Omit<Award, "awardId">;

/**
 * A person on an Award, before the server gives their certificate an ID. Their
 * certificate goes to their Users (at a school, the child's parents), stored
 * as `parentUserIds`.
 */
export type Awardee = {
  firstName: string;
  surname: string;
  parentUserIds: string[];
};

/** What's stored when a Certificate is sent: its files and the direct notices it went out as. */
export type SentCertificateRecord = {
  pngUrl: string;
  pdfUrl: string;
  /** One per User reached. */
  noticeIds: string[];
};

/** One Awardee's copy within an Award. Sent fields stay empty until it's sent. */
export type Certificate = Awardee &
  Partial<SentCertificateRecord> & {
    certificateId: string;
    awardId: string;
    sentAt?: string;
  };

/** A sent Certificate as a child's Family record lists it. */
export type ChildCertificate = {
  certificateId: string;
  awardId: string;
  awardTitle: string;
  certificateTitle: string;
  sentAt: string;
};

/** "Thandi Mokoena" */
export const awardeeFullName = (
  awardee: Pick<Awardee, "firstName" | "surname">,
) => `${awardee.firstName} ${awardee.surname}`;

/** Offered while typing the certificate title. Any other title can be typed too. */
export const CERTIFICATE_TITLE_SUGGESTIONS = [
  "Certificate of Achievement",
  "Certificate of Participation",
  "Certificate of Appreciation",
  "Certificate of Completion",
];

/** "  Van  der Merwe " → "Van der Merwe": how a typed name is stored. */
export const tidyName = (name: string) => name.trim().replace(/\s+/g, " ");

const nameKey = (name: string) => tidyName(name).toLowerCase();

const parentsKey = (parentUserIds: string[]) =>
  Array.from(new Set(parentUserIds)).sort().join(",");

/**
 * The same Awardee on one Award: same first name and surname (ignoring case
 * and extra spaces) and the same Users. The server checks this as well.
 */
export function isSameAwardee(a: Awardee, b: Awardee): boolean {
  return (
    nameKey(a.firstName) === nameKey(b.firstName) &&
    nameKey(a.surname) === nameKey(b.surname) &&
    parentsKey(a.parentUserIds) === parentsKey(b.parentUserIds)
  );
}

/**
 * The child on a parent's Family record, as an Awardee:
 * their name, and the record's parent plus every linked guardian. The server
 * matches certificates to this by name and a shared parent.
 */
export function awardeeFromFamilyRecord(
  child: Pick<ParentChild, "firstName" | "lastName" | "guardians">,
  parentUserId: string,
): Awardee {
  const parentUserIds = Array.from(
    new Set([
      parentUserId,
      ...(child.guardians ?? []).map((guardian) => guardian.userId),
    ]),
  ).filter(Boolean);
  return { firstName: child.firstName, surname: child.lastName, parentUserIds };
}

/** A Family child picked as an Awardee: their name, and their linked parents as Users, as if typed in. */
export function awardeeFromFamily(entry: FamilySearchIndexEntry): Awardee {
  return {
    firstName: tidyName(entry.firstName),
    surname: tidyName(entry.lastName),
    parentUserIds: entry.guardianUserIds,
  };
}

export type AwardDetailsProblems = Partial<Record<keyof AwardDetails, string>>;

/** What's missing before an Award can be saved, keyed by field. Empty when it's ready. */
export function awardDetailsProblems(
  details: AwardDetails,
): AwardDetailsProblems {
  const problems: AwardDetailsProblems = {};
  if (!details.title.trim()) problems.title = "Give this Award a title.";
  if (!details.certificateTemplateId) {
    problems.certificateTemplateId = "Choose a certificate template.";
  }
  if (!details.certificateTitle.trim())
    problems.certificateTitle = "Give the certificate a title.";
  if (!details.achievementName.trim())
    problems.achievementName = "Name the achievement.";
  if (!details.completionDate)
    problems.completionDate = "Choose the completion date.";
  if (details.idPrefix.length < 2) {
    problems.idPrefix = "Use 2 to 10 letters or numbers, like MATH.";
  }
  if (details.signatures.length === 0) problems.signatures = "Pick a signature.";
  return problems;
}

/** ID prefixes are 2–10 upper-case letters or numbers, so "ma-th" becomes "MATH" as it's typed. */
export function normaliseIdPrefix(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);
}

/** "MATH-2026-0001": what the first certificate on this Award would be numbered. */
export function awardCertificateIdPreview(
  idPrefix: string,
  completionDate: string,
): string {
  const year = /^(\d{4})-/.exec(completionDate)?.[1];
  return idPrefix && year ? `${idPrefix}-${year}-0001` : "";
}

/** A blank Award, completed today and using the built-in certificate template. */
export function newAwardDetails(today: Date): AwardDetails {
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    title: "",
    certificateTemplateId: BUILT_IN_CERTIFICATE_TEMPLATE_ID,
    certificateTitle: CERTIFICATE_TITLE_SUGGESTIONS[0],
    achievementName: "",
    description: "",
    completionDate: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
    idPrefix: "",
    signatures: [],
  };
}

/** True when two sets of Award details would save the same thing. */
export function isSameAwardDetails(a: AwardDetails, b: AwardDetails): boolean {
  const keys: (keyof AwardDetails)[] = [
    "title",
    "certificateTemplateId",
    "certificateTitle",
    "achievementName",
    "description",
    "completionDate",
    "idPrefix",
  ];
  return (
    keys.every((key) => a[key] === b[key]) &&
    a.signatures.length === b.signatures.length &&
    a.signatures.every((signature, i) => isSameSignature(signature, b.signatures[i]))
  );
}

const isSameSignature = (a: SignatureInput, b: SignatureInput) =>
  a.name === b.name && a.title === b.title;

/** Puts a signature in the first or second place on an Award. */
export function withSignatureAt(
  signatures: SignatureInput[],
  index: number,
  signature: SignatureInput,
): SignatureInput[] {
  const next = [...signatures];
  next[Math.min(index, next.length)] = signature;
  return next;
}

/** Clears one place; clearing the first moves the second up, so there's never only a second. */
export function withoutSignature(
  signatures: SignatureInput[],
  index: number,
): SignatureInput[] {
  return signatures.filter((_, i) => i !== index);
}

/** Just the parts of an Award a Team member edits. */
export function awardDetails(award: SavedAward): AwardDetails {
  return {
    title: award.title,
    certificateTemplateId: award.certificateTemplateId,
    certificateTitle: award.certificateTitle,
    achievementName: award.achievementName,
    description: award.description,
    completionDate: award.completionDate,
    idPrefix: award.idPrefix,
    // Awards saved before signatures existed have none, and must pick one before saving again.
    signatures: award.signatures ?? [],
  };
}

/** The files a certificate can be downloaded as. */
export const CERTIFICATE_FORMATS = ["png", "pdf"] as const;
export type CertificateFormat = (typeof CERTIFICATE_FORMATS)[number];

/** "Thandi-Mokoena-MATH-2026-0001.png": a downloaded certificate, named after the Awardee and its ID. */
export function certificateFileName(
  certificate: Pick<Certificate, "firstName" | "surname" | "certificateId">,
  format: CertificateFormat,
): string {
  const name = [
    certificate.firstName,
    certificate.surname,
    certificate.certificateId,
  ]
    .join(" ")
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  return `${name}.${format}`;
}

/** Where a sent Certificate's file was stored, if it was. */
export function storedFileUrl(
  certificate: Pick<Certificate, "pngUrl" | "pdfUrl">,
  format: CertificateFormat,
): string | undefined {
  return format === "png" ? certificate.pngUrl : certificate.pdfUrl;
}

/** An Award can only be deleted while none of its certificates has been sent. The server checks this as well. */
export function canDeleteAward(
  certificates: Pick<Certificate, "sentAt">[],
): boolean {
  return certificates.every((certificate) => !certificate.sentAt);
}

/** The direct notice each User gets with the Awardee's certificate attached. */
export function certificateNotice(
  awardTitle: string,
  awardee: Pick<Awardee, "firstName" | "surname">,
): { title: string; body: string } {
  return {
    title: `Special Delivery – Certificate for ${awardeeFullName(awardee)}`,
    body: `${awardee.firstName}'s ${awardTitle} certificate is attached, as an image and as a PDF you can print.`,
  };
}

/** How one Send to all went, by Awardee name. */
export type SendToAllResult = {
  usersReached: number;
  /** Still unsent, so the next Send to all tries them again. */
  notSent: string[];
  /** Marked sent, but not every User got their notice. */
  partlySent: string[];
  /** Reached Users, but couldn't be marked sent, so it still shows as unsent. */
  notRecorded: string[];
};

/** The toast shown once Send to all finishes, e.g. "Sent to 28 Users". */
export function sendToAllMessage(result: SendToAllResult): {
  message: string;
  severity: "success" | "warning" | "error";
} {
  const { usersReached, notSent, partlySent, notRecorded } = result;
  const certificatesOf = (names: string[]) =>
    names.length === 1
      ? `${names[0]}'s certificate`
      : `the certificates for ${names.join(", ")}`;
  const problems: string[] = [];
  if (notSent.length > 0) {
    problems.push(
      `Couldn't send ${certificatesOf(notSent)}. Press Send to all to try again.`,
    );
  }
  if (partlySent.length > 0) {
    problems.push(`Not every User got ${certificatesOf(partlySent)}.`);
  }
  if (notRecorded.length > 0) {
    problems.push(
      `Users got ${certificatesOf(notRecorded)}, but it still shows as not sent. Don't send it again.`,
    );
  }

  const sent = `Sent to ${usersReached} ${usersReached === 1 ? "User" : "Users"}`;
  if (problems.length === 0) return { message: sent, severity: "success" };
  if (usersReached === 0)
    return { message: problems.join(" "), severity: "error" };
  return { message: [`${sent}.`, ...problems].join(" "), severity: "warning" };
}

/** How one Resend went. */
export type ResendResult = {
  /** The Awardee's full name. */
  name: string;
  usersReached: number;
  usersTotal: number;
  /** Whether the new notices were saved on the Certificate, so deleting it withdraws them. */
  recorded: boolean;
};

/** The toast shown once Resend finishes. */
export function resendMessage(result: ResendResult): {
  message: string;
  severity: "success" | "warning" | "error";
} {
  const { name, usersReached, usersTotal, recorded } = result;
  if (usersReached === 0) {
    return {
      message: `Couldn't resend ${name}'s certificate. Check your connection and try again.`,
      severity: "error",
    };
  }
  if (!recorded) {
    return {
      message: `Sent ${name}'s certificate again, but couldn't save that it was sent. Deleting the certificate won't remove these new notices.`,
      severity: "warning",
    };
  }
  if (usersReached < usersTotal) {
    return {
      message: `Sent ${name}'s certificate again to ${usersReached} of ${usersTotal} Users.`,
      severity: "warning",
    };
  }
  return {
    message: `Sent ${name}'s certificate again to ${usersReached} ${usersReached === 1 ? "User" : "Users"}`,
    severity: "success",
  };
}
